/* Query-backed directory. Liquid applies visibility rules before returning links. */
(function (root) {
  function endpoint(base, query) {
    const url = new URL(base, 'https://directory.invalid');
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'collection');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('section_id', 'mf-store-directory-results');
    return url.pathname + url.search;
  }
  function collectionPath(value) {
    if (typeof value !== 'string') return null;
    // Shopify appends predictive-search attribution. Validate the original local
    // pathname first, then discard attribution/hash without normalizing bad paths.
    const pathname = value.split(/[?#]/, 1)[0];
    if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[a-z0-9-]+$/i.test(pathname)) return null;
    return pathname;
  }
  function imagePath(value, origin = root.location && root.location.origin) {
    if (typeof value !== 'string' || !value || /[<>"\\]/.test(value)) return null;
    try {
      const url = new URL(value.startsWith('//') ? 'https:' + value : value, origin || 'https://directory.invalid');
      if (url.protocol !== 'https:') return null;
      if (url.hostname === 'cdn.shopify.com') return url.href;
      if (origin && url.origin === origin && /^\/cdn\/shop\//.test(url.pathname)) return url.href;
      // Relative Shopify CDN paths remain relative when no browser origin exists.
      if (value.startsWith('/cdn/shop/') && /^\/cdn\/shop\//.test(url.pathname)) return url.pathname + url.search;
      return null;
    } catch (_) { return null; }
  }
  function parseResults(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const list = doc.querySelector('[data-store-directory-response]');
    if (!list) throw new Error('Invalid directory response');
    return Array.from(list.querySelectorAll('a[data-store-collection]')).flatMap(link => {
      const url = collectionPath(link.getAttribute('href'));
      return url ? [{ title: link.textContent, url, image: imagePath(link.getAttribute('data-image')) }] : [];
    });
  }
  function createSearch({ fetchImpl, base, parse = parseResults, render }) {
    let generation = 0, controller;
    return async function search(query, final = false) {
      const current = ++generation;
      if (controller) controller.abort();
      controller = new AbortController();
      query = query.trim();
      if (!query) { render({ query, final, state: 'empty', results: [] }); return; }
      render({ query, final, state: 'loading', results: [] });
      try {
        const response = await fetchImpl(endpoint(base, query), { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Directory search failed');
        const results = parse(await response.text());
        if (current === generation) render({ query, final, state: 'ready', results });
      } catch (error) {
        if (current === generation && error.name !== 'AbortError') render({ query, final, state: 'error', results: [] });
      }
    };
  }
  function mount(section) {
    if (section.dataset.directoryMounted) return;
    section.dataset.directoryMounted = 'true';
    const input = section.querySelector('.local-search-input');
    const suggestions = section.querySelector('.local-search-suggestions');
    const results = section.querySelector('.local-search-final-results');
    const status = section.querySelector('.local-search-searched-text');
    const form = input.closest('form');
    const max = Math.min(10, Math.max(1, Number(section.dataset.suggestionLimit) || 5));
    function element(tag, className, text) {
      const node = document.createElement(tag); node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }
    const search = createSearch({ fetchImpl: window.fetch.bind(window), base: section.dataset.searchUrl, render({ query, final, state, results: matches }) {
      const target = final ? results : suggestions;
      target.replaceChildren();
      if (final) { suggestions.replaceChildren(); suggestions.style.display = 'none'; status.textContent = query ? `Searched for “${query}”` : ''; }
      else { results.replaceChildren(); status.textContent = ''; suggestions.style.display = query ? 'block' : 'none'; }
      if (state === 'empty') return;
      if (state === 'loading') { target.append(element('p', 'suggestions-item', 'Searching…')); return; }
      if (state === 'error') { target.append(element('p', 'suggestions-item', 'Search is unavailable. Please try again.')); return; }
      if (!matches.length) { target.append(element('p', 'suggestions-item', 'No stores found.')); return; }
      const list = element('ul', final ? 'local-collection-grid' : 'suggestions-list');
      matches.slice(0, final ? 10 : max).forEach(match => {
        const li = element('li', final ? 'local-collection-card' : 'suggestions-item');
        const link = element('a', final ? 'local-collection-card-title' : 'suggestion-link', match.title);
        link.href = match.url;
        if (match.image) { const image = element('img', ''); image.src = match.image; image.alt = ''; image.loading = 'lazy'; li.append(image); }
        li.append(link); list.append(li);
      });
      target.append(list);
      if (!final) {
        const button = element('button', 'suggestions-cta', `Search for “${query}”`);
        button.type = 'button'; button.addEventListener('click', () => search(input.value, true)); target.append(button);
      }
    }});
    // Immediate cancellation prevents stale results while a new query is entered.
    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer); search('');
      if (input.value.trim()) timer = setTimeout(() => search(input.value), 200);
    });
    form.addEventListener('submit', event => { clearTimeout(timer); event.preventDefault(); search(input.value, true); });
  }
  const api = { endpoint, collectionPath, imagePath, createSearch, parseResults, mount };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root.document) {
    const init = () => document.querySelectorAll('[data-store-directory]').forEach(mount);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
    document.addEventListener('shopify:section:load', init);
  }
})(typeof window !== 'undefined' ? window : globalThis);
